import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { useStoreConfig } from '@/contexts/StoreConfigContext';

const SESSION_KEY = 'florisimo_announcement_closed_v1';

export function AnnouncementModal() {
  const { storeConfig } = useStoreConfig();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Only show if: enabled in settings AND has text AND not seen this session
    if (storeConfig.showAnnouncement && storeConfig.announcementText) {
      const alreadySeen = localStorage.getItem(SESSION_KEY);
      if (!alreadySeen) {
        // Small delay for better UX - let the page load first
        const timer = setTimeout(() => setOpen(true), 8000);
        return () => clearTimeout(timer);
      }
    }
  }, [storeConfig.showAnnouncement, storeConfig.announcementText]);

  const handleClose = () => {
    localStorage.setItem(SESSION_KEY, 'true');
    setOpen(false);
  };

  if (!storeConfig.showAnnouncement || !storeConfig.announcementText) {
    return null;
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none"
            dir="rtl"
          >
            <div className="relative bg-white/95 backdrop-blur-xl rounded-2xl border border-primary/10 shadow-2xl max-w-md w-full p-8 pointer-events-auto">
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 left-4 p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                aria-label="סגור"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Content */}
              <div className="text-center space-y-6">
                {/* Logo */}
                <div className="space-y-2">
                  <h2 className="font-display text-2xl tracking-[0.35em] uppercase text-primary">
                    FLORISIMO
                  </h2>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-12 h-px bg-primary/30" />
                    <Sparkles className="w-4 h-4 text-primary/50" />
                    <div className="w-12 h-px bg-primary/30" />
                  </div>
                </div>

                {/* Announcement Text */}
                <p className="font-serif text-xl leading-relaxed text-foreground">
                  {storeConfig.announcementText}
                </p>

                {/* Decorative bottom */}
                <div className="pt-2">
                  <div className="w-16 h-px bg-primary/20 mx-auto" />
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
